import type { SearchComponent } from '@ad-lucy/components/builderComponents/HeaderSearch/raw/Utils/types';
import { LoggerService } from '@ad-lucy/components/LoggerService';
import { filterGridByBreakpointOption } from '@ad-lucy/components/layout/Grid/filterGridByBreakpointOption';
import { getGridByCodeOrId } from '@ad-lucy/components/layout/Grid/getGridByCodeOrId';
import type { GridCombinedList } from '@ad-lucy/components/layout/Grid/types';
import type {
  CreativesConfigComponents,
  CreativesConfigComponentWrapper,
} from '@ad-lucy/config/creatives';
import type { KnownBreakpoints } from '@ad-lucy/context/ThemeProvider';
import { type ImageMetaObject, UriUtils } from '@ad-lucy/utils';
import React, { type ReactElement, Suspense } from 'react';

import type { ComponentConfig, RawMeta } from '../../types';
import type {
  GridInterface,
  OrderedMapOfComponentInstances,
  Page,
  RawCategory,
  RawCategoryStage,
  RawComponentInstance,
  RawContentConfig,
  Tile,
  TileComponent,
} from '../types';

const DURATION_DEFAULT = 30;

export type TransformComponentsAPIFunc = (
  components: Partial<CreativesConfigComponents>,
  gridList: GridCombinedList,
  data: RawContentConfig,
) => {
  transformCategoriesToPages: (rawCategories: RawCategory[] | undefined) => Page[];
  transformOrderedMapOfComponents: (
    mapOfComponents: OrderedMapOfComponentInstances,
  ) => ReactElement[][];
  transformSearchResults: (rawCategories: RawCategory[] | undefined) => SearchComponent[];
};

const getPageDuration = (stage: RawCategoryStage): number =>
  Number(stage.meta?.duration) || DURATION_DEFAULT;

export const parseLinkout = (link: string, params: string): string => {
  const { replaceUrlVariables, attachQueryParams } = UriUtils;
  return attachQueryParams(replaceUrlVariables(link), replaceUrlVariables(params));
};

export const parseComponentMeta = (
  name: string,
  meta: RawMeta | null,
  defaultMetaData?: RawMeta,
): RawMeta => {
  if (!meta) {
    return {};
  }

  const newMeta = new Map();

  // In the past the name of components in the Database changed.
  // For it to not be a pain again to refactor our frontend code, the component name is here removed from the property name.
  Object.entries(meta).forEach(([key, value]) => {
    // Use default meta data if value is null or empty
    const val = value ?? defaultMetaData?.[key];
    const newKey = typeof key === 'string' ? key.replace(`${name}_`, '') : key;
    newMeta.set(newKey, val);
  });

  return {
    ...defaultMetaData,
    ...Object.fromEntries(newMeta),
  };
};

const filterTilesByGrid = (tiles: Tile[], grid: GridInterface): Tile[] => {
  return tiles.filter((tile) =>
    grid.rows.find((row) => row.columns.find((column) => column.tileId === tile.id)),
  );
};

const LEGACY_MOBILE_OPTIONS_MAP = {
  '24_1': '2:3',
  '24_2': '1:1',
} as Record<string, string>;

const getGridBreakpointOptions = (
  rawStage: RawCategoryStage,
): Record<KnownBreakpoints, string | null | undefined> => {
  return {
    sitebar: null,
    hpa: null,
    mobile: (rawStage.meta?.gridMobile ??
      LEGACY_MOBILE_OPTIONS_MAP[rawStage.meta?.gridMobile_1_1_1 as string]) as
      | string
      | null
      | undefined,
    billboard: null,
  };
};

export const transformComponentsAPI: TransformComponentsAPIFunc = (components, gridList) => {
  const getComponent = (
    n: keyof CreativesConfigComponents,
  ): CreativesConfigComponentWrapper | undefined => {
    if (Object.keys(components).indexOf(n) !== -1) {
      return components[n]?.wrapper as CreativesConfigComponentWrapper;
    }
    return undefined;
  };

  const renderComponentInstances = (
    componentInstances: RawComponentInstance[],
    parent?: ComponentConfig,
  ): TileComponent[] => {
    const logger = LoggerService.getLogger().getModuleLogger(renderComponentInstances.name);
    const renderToJsxElement = (
      compInstances: RawComponentInstance[],
      parentWithoutChildren?: ComponentConfig,
    ): ReactElement[] =>
      renderComponentInstances(compInstances, parentWithoutChildren).map(({ element }) => element);

    const getContainerChildren = (
      children: RawComponentInstance[],
      config: ComponentConfig,
    ): ReactElement[] | ReactElement[][] => {
      const childrenAreSliderContainers = children.every(
        (child) => child.component?.name === 'sliderContainer',
      );

      if (childrenAreSliderContainers) {
        const containers: ReactElement[][] = [];
        children.forEach((child) => {
          if (child.children) {
            containers.push(renderToJsxElement(child.children, config));
          }
        });
        return containers;
      }

      return renderToJsxElement(children, config);
    };

    return componentInstances
      .map((componentInstance): TileComponent | undefined => {
        const componentIdentifier = componentInstance.component;

        if (!componentIdentifier) {
          logger.error('Cannot render component: Missing component identifier.', componentInstance);
          return undefined;
        }
        const component = getComponent(componentIdentifier.name);

        if (!component) {
          logger.error(
            `Cannot render component: No component with the name "${componentIdentifier.name}" is registered in the creatives configuration.`,
          );
          return undefined;
        }

        let config: ComponentConfig = {
          ...componentInstance,
          parent,
          meta: parseComponentMeta(
            componentIdentifier.name,
            componentInstance.meta,
            components[componentIdentifier.name]?.defaultMetaData as RawMeta,
          ),
          page: componentInstance.stage?.id ?? undefined,
          component: componentIdentifier,
          product: componentInstance.product?.id,
          children: undefined,
        };

        config = {
          ...config,
          children: componentInstance.children
            ? getContainerChildren(componentInstance.children, config)
            : undefined,
        };

        const element = (
          <Suspense key={componentInstance.id} fallback="">
            {React.createElement(component, config)}
          </Suspense>
        );

        return {
          id: componentInstance.id,
          meta: config.meta ?? {},
          name: componentIdentifier.name,
          tileId: config.column,
          element,
        };
      })
      .filter((component): component is TileComponent => component !== undefined);
  };

  const componentsToTiles = (componentInstances: RawComponentInstance[]): Tile[] => {
    return renderComponentInstances(componentInstances).reduce<Tile[]>((tiles, component) => {
      const ourTile = tiles.find((tile) => tile.id === component.tileId);

      if (!ourTile) {
        tiles.push({
          id: component.tileId,
          components: [component],
        });
        return tiles;
      }

      ourTile.components.push(component);
      return tiles;
    }, []);
  };

  // Sort the stages by the showon value if it exists, otherwise sort by sorting.
  const sortStages = (a: Page, b: Page) => {
    if ('showon' in a.meta && 'showon' in b.meta) {
      return Number(a.meta.showon) - Number(b.meta.showon);
    }
    return a.sorting - b.sorting;
  };

  // In getPagesFromCategory, we retrieve the grids for the current stage and then filter them.
  // This filter removes the 'Mobile 3/3' grid when mobileVariant is '24_2'.
  // After filtering, we iterate over the remaining grids and adjust them if needed.
  const getPagesFromCategory = (rawCategory: RawCategory): Page[] =>
    rawCategory.stages
      .reduce<Page[]>((acc, rawStage) => {
        let componentInstances: RawComponentInstance[] = [];
        if (rawStage.componentInstances) {
          componentInstances = rawStage.componentInstances
            .filter((compInstance): compInstance is RawComponentInstance => !!compInstance)
            .sort((a, b) => a.sorting - b.sorting);
        }
        const tiles = componentsToTiles(componentInstances);

        // Retrieve grids for the current stage
        const grids = getGridByCodeOrId(gridList, rawStage.grid, rawStage.gridCode);
        const filteredGrids = filterGridByBreakpointOption(
          grids,
          getGridBreakpointOptions(rawStage),
        );

        filteredGrids.forEach((grid, subId) => {
          acc.push({
            id: rawStage.id,
            categoryId: rawCategory.id,
            subId,
            name: rawStage.name,
            sorting: rawStage.sorting * 1000, // Multiply sorting to make space for subpages.
            ...(rawStage.start != null && { start: rawStage.start }),
            ...(rawStage.end != null && { end: rawStage.end }),
            grid,
            tiles: filterTilesByGrid(tiles, grid),
            meta: {
              ...rawStage.meta,
              ...(typeof rawStage.meta?.stageLinkOutDefault === 'string' && {
                stageLinkOutDefault: rawStage.meta.stageLinkOutDefault,
              }),
              duration: getPageDuration(rawStage),
            },
          });
        });

        return acc;
      }, [])
      .sort(sortStages);

  const transformCategoriesToPages = (rawCategories: RawCategory[] | undefined): Page[] =>
    rawCategories?.flatMap(getPagesFromCategory) ?? [];

  const transformOrderedMapOfComponents = (
    mapOfComponents: OrderedMapOfComponentInstances,
  ): ReactElement[][] =>
    Object.entries(mapOfComponents)
      // Sort the items according to their ids
      .sort(([idA], [idB]) => {
        if (Number(idA) > Number(idB)) return 1;
        return -1;
      })
      // Take into account empty sections
      .reduce(
        (acc, [key, value]) => {
          while (Number(key) > acc.length) {
            acc.push([`${acc.length}`, []]);
          }
          acc.push([key, value ?? []]);
          return acc;
        },
        [] as [string, RawComponentInstance[]][],
      )
      // Create an array only of RawComponentInstances
      .flatMap(([, rawComponentInstances]): RawComponentInstance[][] | [] => {
        // Sort the RawComponentInstances according to their sorting property
        return [rawComponentInstances.sort((a, b) => a.sorting - b.sorting)];
      })
      .map((componentInstance) => renderComponentInstances(componentInstance))
      .map((comps: TileComponent[]): ReactElement[] => comps.map(({ element }) => element));

  const transformSearchResults = (rawCategories: RawCategory[] | undefined): SearchComponent[] => {
    if (!rawCategories || rawCategories.length === 0) {
      return [];
    }

    const rawStages: RawCategoryStage[] = [];
    rawCategories.forEach((rawCategory) => {
      rawCategory.stages.map((stage) => rawStages.push(stage));
    });

    const searchConfig: SearchComponent[] = [];
    rawStages.forEach((stage) => {
      stage.componentInstances?.forEach((rawComponent) => {
        if (rawComponent) {
          const { column, component: componentData, meta, name, product } = rawComponent;
          if (componentData) {
            const { id, name: compName } = componentData;
            searchConfig.push({
              compName,
              id,
              image:
                (id === '27'
                  ? (meta?.imageMap_image as ImageMetaObject)
                  : (meta?.image_l as ImageMetaObject)) || undefined,
              name: name ?? '',
              placement: {
                column,
                gridId: stage.grid,
                page: stage.id,
              },
              product: {
                category: meta?.product_category as string,
                id: product?.id,
                name: meta?.product_name as string,
                reference: meta?.product_reference as string,
              },
              textBody:
                (meta?.text_body as string) ||
                (meta?.hover_text as string) ||
                (meta?.stickyText_body as string),
            });
          }
        }
      });
    });

    return searchConfig;
  };

  return {
    transformCategoriesToPages,
    transformOrderedMapOfComponents,
    transformSearchResults,
  };
};
